import { ref, computed,inject, nextTick } from "vue";

import {
    setColorsToStorage,
    getColorsFromStorage,
    setDimmingParamsToStorage,
    getDimmingParamsFromStorage,
    generateColorPalette,
    THEMES,
    DEFAULT_DIM_FACTOR_ICON,
    DEFAULT_DISABLED_BUTTON_PARAMS,
    DEFAULT_DISABLED_BG_PARAMS,
    DEFAULT_DIM_FACTOR,
    DEFAULT_FONT_WEIGHT,
    DEFAULT_BG_WEIGHT,
} from "../Helpers/colorPalette.js";
import { hexToRgba, validateColorCombination } from "../Utilities/Colour.js";
import utilities from "../Helpers/utilities.js";

export function useColorTheme(theme) {
    const initialPalette = getColorsFromStorage();
    const {  showInteractiveSnackbar } = utilities();

    const globalColorPalette = ref({ ...initialPalette });
    const primaryColors = ref({
        primary: initialPalette.primary,
        secondary: initialPalette.secondary,
        fontLight: initialPalette.fontLight,
    });

    // Load stored dimming parameters or use defaults
    const storedDimmingParams = getDimmingParamsFromStorage();
    const dimmingParams = ref(
        storedDimmingParams || {
            dimFactor: DEFAULT_DIM_FACTOR,
            fontWeight: DEFAULT_FONT_WEIGHT,
            bgWeight: DEFAULT_BG_WEIGHT,
            dimFactorIcon: DEFAULT_DIM_FACTOR_ICON,
        },
    );

    // Store color adjustments for user notifications
    const colorAdjustments = ref([]);

    const isCustomTheme = computed(() => {
        const currentColors = primaryColors.value;
        const matchesAnyTheme = Object.values(THEMES).some((themeColors) => {
            return (
                currentColors.primary === themeColors.primary &&
                currentColors.secondary === themeColors.secondary &&
                currentColors.fontLight === themeColors.fontLight
            );
        });
        return !matchesAnyTheme;
    });

    function updatePrimaryColor(colorKey, value) {
        const rgbaValue = value.startsWith("#") ? hexToRgba(value) : value;

        // Create a temporary copy with the new color
        const tempColors = {
            ...primaryColors.value,
            [colorKey]: rgbaValue,
        };

        // Validate the color combination - pass the colorKey to only validate relevant combinations
        const validation = validateColorCombination(
            tempColors.primary,
            tempColors.secondary,
            tempColors.fontLight,
            colorKey, // Pass which color is being changed for targeted validation
        );

        // Apply the validated/adjusted colors (including any auto-adjustments)
        primaryColors.value = {
            primary: validation.primary,
            secondary: validation.secondary,
            fontLight: validation.fontLight,
        };

        // Store adjustments for user notification
        colorAdjustments.value = validation.adjustments;

        // If there were auto-adjustments, notify the user
        if (validation.adjustments.length > 0) {
            const autoAdjusted = validation.adjustments.filter(
                (adj) => adj.wasAutoAdjusted,
            );

            if (autoAdjusted.length > 0) {
                const adjustedColorTypes = autoAdjusted.map(
                    (adj) => adj.colorType,
                );
                const hasPrimary = adjustedColorTypes.includes("primary");
                const hasFontLight = adjustedColorTypes.includes("fontLight");

                let message;
                if (hasPrimary && hasFontLight) {
                    message =
                        "The background and font colors have been automatically adjusted to ensure proper contrast for better readability.";
                } else {
                    const adjustmentMessages = autoAdjusted
                        .map((adj) => adj.reason)
                        .filter((reason) => reason && reason.trim().length > 0);
                    message = adjustmentMessages.join(" ");
                }

                if (message && message.trim().length > 0) {
                    setTimeout(() => {
                        showInteractiveSnackbar(message, null, "info", 5000);
                    }, 100);
                }
            }
        }

        saveTheme(primaryColors.value);
        return true; // Always return true - adjustments are applied automatically
    }

    function applyTheme(themeKey) {
        const themeColors = THEMES[themeKey];

        // Predefined themes are pre-validated and trusted
        // Apply them directly without validation
        primaryColors.value = {
            primary: themeColors.primary,
            secondary: themeColors.secondary,
            fontLight: themeColors.fontLight,
        };

        // Clear any previous adjustments
        colorAdjustments.value = [];

        saveTheme(primaryColors.value);
        return true; // Always succeeds for predefined themes
    }

    function clearColorAdjustments() {
        colorAdjustments.value = [];
    }

    function updateDimmingSettings(
        dimFactor,
        fontWeight,
        bgWeight,
        dimFactorIcon,
    ) {
        // Update dimming parameters
        dimmingParams.value = {
            dimFactor,
            fontWeight,
            bgWeight,
            dimFactorIcon,
        };

        // Save dimming parameters to storage
        setDimmingParamsToStorage(dimmingParams.value);

        // Regenerate the color palette with new dimming settings
        saveTheme(primaryColors.value, dimmingParams.value);
    }

    function saveTheme(primaryColours, customDimmingParams = null) {
        const params = customDimmingParams || dimmingParams.value;

        // If dimming params are the default/initial values, pass null to trigger auto-calculation
        // Otherwise, use the custom values for manual override
        const shouldAutoCalculate =
            !customDimmingParams &&
            params.dimFactor === 0.7 &&
            params.fontWeight === 0.4 &&
            params.bgWeight === 0.9;

        const colors = generateColorPalette(
            primaryColours,
            undefined, // Use default brightness config
            shouldAutoCalculate ? null : params.dimFactor,
            shouldAutoCalculate ? null : params.fontWeight,
            shouldAutoCalculate ? null : params.bgWeight,
            params.dimFactorIcon || DEFAULT_DIM_FACTOR_ICON,
            DEFAULT_DISABLED_BUTTON_PARAMS, // Always use default button params
            DEFAULT_DISABLED_BG_PARAMS, // Always use default disabled bg params
        );
        globalColorPalette.value = colors;
        setColorsToStorage(colors);
        applyColorsToTheme(colors);
        applyCSSCustomProperties(colors);
    }

    function applyColorsToTheme(colors) {
        Object.entries(colors).forEach(([key, value]) => {
            // Skip opacity values as they're not colors
            if (
                key !== "inactiveOpacity" &&
                key !== "disassociatedMemberOpacity"
            ) {
                nextTick(() => {
                    if(theme){
                        theme.themes.value.light.colors[key] = value;
                    }
                });
            }
        });
    }

    function applyCSSCustomProperties(colors) {
        Object.entries(colors).forEach(([key, value]) => {
            // Handle opacity values separately as they're numbers, not colors
            if (key === "inactiveOpacity") {
                document.documentElement.style.setProperty(
                    `--inactive-opacity`,
                    value,
                );
            } else if (key === "disassociatedMemberOpacity") {
                document.documentElement.style.setProperty(
                    `--disassociated-member-opacity`,
                    value,
                );
            } else {
                document.documentElement.style.setProperty(
                    `--v-theme-${key}`,
                    value,
                );
            }
        });
    }

    function initialize() {
        applyColorsToTheme(globalColorPalette.value);
        applyCSSCustomProperties(globalColorPalette.value);
    }

    return {
        initialize,
        applyTheme,
        saveTheme,
        primaryColors,
        updatePrimaryColor,
        globalColorPalette,
        colorAdjustments,
        clearColorAdjustments,
        isCustomTheme,
        updateDimmingSettings,
        dimmingParams,
    };
}
